import { Card, CardContent, CardHeader, CardTitle } from '@predoc/cow';
import { DocumentTemplateShell, TemplateEmptyState } from './TemplateShared';
import { DASH, formatTimestamp, type TemplatePatient } from './templateUtils';

export interface VitalsTemplateRow {
  id?: string;
  name?: string;
  value?: string;
  unit?: string;
  observedAt?: string;
  dateUploaded?: string;
  category?: string;
  text?: string;
}

export type VitalsTemplatePatient = TemplatePatient;

export interface VitalsTemplateGroup {
  id?: string;
  observedAt?: string;
  vitals: VitalsTemplateRow[];
}

export interface VitalsTemplateProps {
  groups: VitalsTemplateGroup[];
  patient?: VitalsTemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightVitalId?: string;
}

const tableHeaders = ['Vital', 'Value', 'Unit', 'Notes'] as const;

interface VitalsGroupSectionProps {
  group: VitalsTemplateGroup;
  index: number;
  highlightVitalId?: string;
}

const VitalsGroupSection = ({
  group,
  index,
  highlightVitalId,
}: VitalsGroupSectionProps) => {
  const vitals = group.vitals ?? [];
  const groupTime = formatTimestamp(
    group.observedAt ?? vitals[0]?.observedAt,
    '',
  );

  return (
    <Card className="flex flex-col overflow-hidden rounded-sm border-2 border-gray-200 bg-white p-0 pb-4 shadow-none">
      <CardHeader className="flex flex-col gap-2.5 rounded-none bg-slate-700 px-5 pt-5 pb-3 text-white">
        <CardTitle className="text-[14px] leading-tight font-semibold text-white">
          {groupTime || `Observation ${index + 1}`}
        </CardTitle>
        <p className="text-[11px] leading-tight">
          <span className="font-semibold">Observations recorded:</span>{' '}
          {vitals.length}
        </p>
      </CardHeader>

      <CardContent className="mx-5 mt-3 overflow-hidden rounded-sm border border-zinc-200 p-0">
        <div className="flex bg-slate-100">
          {tableHeaders.map((header, idx) => (
            <div
              key={header}
              className={`flex flex-1 border-b border-zinc-300 px-3 py-3.5 ${
                idx < tableHeaders.length - 1 ? 'border-r' : ''
              }`}
            >
              <p className="flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
                {header}
              </p>
            </div>
          ))}
        </div>

        {vitals.map((vital, rowIdx) => {
          const isHighlighted =
            !!highlightVitalId && vital.id === highlightVitalId;
          const zebra = rowIdx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
          const rowBg = isHighlighted ? 'bg-amber-100' : zebra;
          const cells = [vital.name, vital.value, vital.unit, vital.text];
          const rowKey = vital.id ?? `${vital.name ?? 'vital'}-${rowIdx}`;

          return (
            <div
              key={rowKey}
              id={vital.id ? `vital-${vital.id}` : undefined}
              tabIndex={isHighlighted ? -1 : undefined}
              className={`flex border-t border-zinc-200 ${rowBg}`}
            >
              {cells.map((cell, colIdx) => (
                <div
                  key={tableHeaders[colIdx]}
                  className={`flex flex-1 px-3 py-3 ${
                    colIdx < cells.length - 1 ? 'border-r border-zinc-200' : ''
                  }`}
                >
                  <p className="flex-1 text-[10px] leading-tight text-zinc-950">
                    {cell?.toString().trim() || DASH}
                  </p>
                </div>
              ))}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export const VitalsTemplate = ({
  groups,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightVitalId,
}: VitalsTemplateProps) => {
  const nonEmptyGroups = (groups ?? []).filter(
    (group) => group.vitals && group.vitals.length > 0,
  );
  const hasGroups = nonEmptyGroups.length > 0;

  return (
    <DocumentTemplateShell
      title="Vitals Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
    >
      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        {!hasGroups ? (
          <TemplateEmptyState message="No vitals available" />
        ) : (
          nonEmptyGroups.map((group, index) => (
            <VitalsGroupSection
              key={group.id ?? `group-${index}`}
              group={group}
              index={index}
              highlightVitalId={highlightVitalId}
            />
          ))
        )}
      </section>
    </DocumentTemplateShell>
  );
};
